/**
 * Reusable card component for mobile list items.
 * Provides consistent styling with optional tap handler and chevron.
 */

import { ChevronRight } from 'lucide-react'

interface MobileCardProps {
  children: React.ReactNode
  onClick?: () => void
  className?: string
  showChevron?: boolean
}

export default function MobileCard({ children, onClick, className = '', showChevron = false }: MobileCardProps) {
  const Wrapper = onClick ? 'button' : 'div'
  return (
    <Wrapper
      onClick={onClick}
      className={`w-full bg-white rounded-xl border border-gray-200 p-4 text-left transition-colors ${
        onClick ? 'active:bg-gray-50 hover:border-indigo-200' : ''
      } ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">{children}</div>
        {showChevron && onClick && (
          <ChevronRight size={16} className="text-gray-300 flex-shrink-0 mt-1" />
        )}
      </div>
    </Wrapper>
  )
}

/**
 * Collapsible section for mobile — tap header to expand/collapse.
 */
import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

export function CollapsibleSection({
  title,
  children,
  defaultOpen = false,
}: {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 active:bg-gray-100"
      >
        {title}
        <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 py-3 border-t border-gray-100">{children}</div>}
    </div>
  )
}
